"use client"

import { cn } from "@/lib/utils"
import {
  Button as MantineButton,
  Skeleton,
  type ButtonProps as MantineButtonProps,
  type ButtonStylesNames,
} from "@mantine/core"
import classes from "./button.module.css"

export type ButtonVariant = "primary" | "secondary" | "ghost" | "dark"
export type ButtonSize = "sm" | "md" | "lg"

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: classes.primary,
  secondary: classes.secondary,
  ghost: classes.ghost,
  dark: classes.dark,
}

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: classes.sizeSm,
  md: classes.sizeMd,
  lg: classes.sizeLg,
}

const SKELETON_SIZE_CLASS: Record<ButtonSize, string> = {
  sm: classes.skeletonSm,
  md: classes.skeletonMd,
  lg: classes.skeletonLg,
}

type ButtonOwnProps = {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Shows a skeleton shimmer instead of the button */
  isLoading?: boolean
  /** Icon node rendered to the left of the label */
  leftIcon?: React.ReactNode
  /** Icon node rendered to the right of the label */
  rightIcon?: React.ReactNode
  /** When true, renders a red notification badge on the top-right corner */
  showNotification?: boolean
  /** Number shown inside the notification badge */
  notificationCount?: number
  className?: string
  classNames?: Partial<Record<ButtonStylesNames, string>>
}

export type ButtonProps = Omit<
  MantineButtonProps,
  | "variant"
  | "size"
  | "color"
  | "radius"
  | "gradient"
  | "leftSection"
  | "rightSection"
  | "loading"
  | "loaderProps"
  | "unstyled"
  | "classNames"
> &
  ButtonOwnProps &
  Omit<React.ComponentPropsWithoutRef<"button">, keyof ButtonOwnProps>

/**
 * Primary design-system button (Figma StreamHub UI Kit).
 * Built on Mantine `Button` with token styles in `button.module.css`.
 */
export function Button({
  variant = "primary",
  size = "sm",
  isLoading = false,
  leftIcon,
  rightIcon,
  showNotification = false,
  notificationCount = 1,
  disabled,
  children,
  className,
  classNames,
  ...props
}: ButtonProps) {
  if (isLoading) {
    return (
      <Skeleton
        className={cn(classes.skeleton, SKELETON_SIZE_CLASS[size], className)}
      />
    )
  }

  const rootClassName = cn(
    classes.root,
    VARIANT_CLASS[variant],
    SIZE_CLASS[size],
    className,
    classNames?.root
  )

  return (
    <span className={classes.wrapper}>
      <MantineButton
        data-slot="button"
        unstyled
        disabled={disabled}
        leftSection={leftIcon}
        rightSection={rightIcon}
        classNames={{
          root: rootClassName,
          inner: cn(classes.inner, classNames?.inner),
          label: cn(classes.label, classNames?.label),
          section: cn(classes.section, classNames?.section),
          loader: classNames?.loader,
        }}
        {...props}
      >
        {children}
      </MantineButton>

      {showNotification && (
        <span
          aria-label={`${notificationCount} notification${notificationCount !== 1 ? "s" : ""}`}
          className={classes.badge}
        >
          <span className={classes.badgeCount}>{notificationCount}</span>
        </span>
      )}
    </span>
  )
}
